export type AuditPeriodShortcut = '24H' | '3D' | '7D' | '30D';

export type AuditChartMetric = 'VISITAS' | 'CHAMADAS';
export type AuditChartGranularity = 'HORA' | 'DIA';

export type AuditMarker = 'PERMITIDO' | 'NEGADO' | 'SEM_REGISTRO';

export const AUDIT_ALL = 'TODOS';

export interface AuditFilters {
  dataInicial: string;
  dataFinal: string;
  tela: string;
  servico: string;
  usuario: string;
  marcador: string;
}

export interface AuditOption {
  id: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Formato dos cards (view model)
// ---------------------------------------------------------------------------

export interface AuditOverview {
  periodo: { inicio: string; fim: string };
  visitas: number;
  telasVisitadas: number;
  totalTelas: number;
  chamadasApi: number;
  chamadasPorVisita: number | null;
  usuarios: number;
  servicosApi: number;
  visitasSemUsuario: number;
}

export interface AuditScreenRank {
  tela: string;
  rota: string;
  visitas: number;
  chamadasApi: number;
  chamadasPorVisita: number | null;
  usuarios: number;
}

export interface AuditServiceRank {
  servico: string;
  chamadas: number;
  percentual: number;
}

export interface AuditMarkerTotal {
  marcador: string;
  total: number;
  percentual: number;
}

export interface AuditEndpoint {
  endpoint: string;
  servico: string;
  metodo: string;
  chamadas: number;
}

export interface AuditUserActivity {
  usuario: string;
  idUsuario: string;
  visitas: number;
  chamadas: number;
  servicos: number;
}

export interface AuditRecentVisit {
  correlationId: string;
  dataHora: string;
  usuario: string;
  idUsuario: string;
  tela: string;
  rota: string;
  apiPath: string;
  chamadas: number;
}

export interface AuditChartPoint {
  label: string;
  valor: number;
}

// ---------------------------------------------------------------------------
// Contrato do back: Painel de Atividades do ms-access-control
// GET /v1/access-control/activity/{recurso}
// Datas em ISO sem fuso, horário local da gravação. Tipos inferidos dos
// exemplos do contrato.
// ---------------------------------------------------------------------------

export interface ActivityQuery {
  correlationRoute?: string;
  service?: string;
  userId?: string;
  marcador?: string;
  dataInicio?: string; // inclusivo
  dataFim?: string; // exclusivo
}

export interface ActivityPeriod {
  periodoInicio: string | null;
  periodoFim: string | null;
}

export interface VisaoGeralResponse extends ActivityPeriod {
  visitas: number;
  telasVisitadas: number;
  totalTelas: number;
  totalChamadas: number;
  mediaChamadasPorVisita: number | null;
  usuarios: number;
  servicos: number;
  visitasSemUsuario: number;
}

export interface SerieTemporalResponse extends ActivityPeriod {
  agrupamento: AuditChartGranularity;
  pontos: { instante: string; visitas: number; chamadas: number }[];
}

export interface TelasResponse extends ActivityPeriod {
  ordenarPor: 'VISITAS' | 'CHAMADAS';
  telas: {
    correlationRoute: string;
    visitas: number;
    chamadas: number;
    mediaChamadasPorVisita: number | null;
    usuarios: number;
  }[];
}

export interface ServicosResponse extends ActivityPeriod {
  totalChamadas: number;
  servicos: { service: string; chamadas: number; percentual: number }[];
}

export interface MarcadoresResponse extends ActivityPeriod {
  totalChamadas: number;
  marcadores: { marcador: string; chamadas: number; percentual: number }[];
}

export interface EndpointsResponse extends ActivityPeriod {
  endpoints: {
    path: string;
    method: string;
    service: string;
    chamadas: number;
    usuarios: number;
  }[];
}

export interface UsuariosResponse extends ActivityPeriod {
  usuarios: {
    id: string;
    nome: string | null;
    visitas: number;
    chamadas: number;
    servicos: number;
  }[];
}

export interface VisitasResponse extends ActivityPeriod {
  visitas: {
    correlationId: string;
    inicio: string;
    fim: string;
    correlationRoute: string;
    apiPath: string;
    usuarioId: string | null;
    usuarioNome: string | null;
    chamadas: number;
  }[];
}

export interface VisitaDetalheResponse {
  correlationId: string;
  correlationRoute: string;
  usuarioId: string | null;
  usuarioNome: string | null;
  inicio: string;
  fim: string;
  totalChamadas: number;
  chamadas: {
    created: string;
    service: string;
    method: string;
    path: string;
    endpoint: string | null;
    // Vazio vem como null ou "".
    queryParams?: string | null;
    marcador: string;
  }[];
}

// Parâmetros próprios de cada recurso, além dos filtros comuns.
export interface ActivityResources {
  'visao-geral': { params: {}; response: VisaoGeralResponse };
  'serie-temporal': {
    params: { agrupamento?: AuditChartGranularity };
    response: SerieTemporalResponse;
  };
  telas: {
    params: { ordenarPor?: 'VISITAS' | 'CHAMADAS'; limite?: number };
    response: TelasResponse;
  };
  servicos: { params: { limite?: number }; response: ServicosResponse };
  marcadores: { params: {}; response: MarcadoresResponse };
  endpoints: { params: { limite?: number }; response: EndpointsResponse };
  usuarios: { params: { limite?: number }; response: UsuariosResponse };
  visitas: { params: { limite?: number }; response: VisitasResponse };
}

export type ActivityResource = keyof ActivityResources;

// GET /v1/usuarios/obter-usuarios-perfis?nome= (ms-usuario). O id é o mesmo
// user._id gravado nas atividades (filtro userId). O cpf também vem, mas não é usado.
export interface UsuarioResumoResponse {
  id: string;
  nome: string;
}
